<script>
import { mapActions, mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import Spinner from 'shared/components/Spinner.vue';
import KnowledgeProcessingModal from './knowledgeBase/KnowledgeProcessingModal.vue';
import {
  clearOnboardingKbSession,
  getOnboardingKbSession,
  saveOnboardingKbSession,
} from '../../../../helper/onboardingKbSession';

// Knowledge API's CreateDatasetSchema.name caps at 20 chars (confirmed via
// scripts/sync_knowledge_api_spec.sh) — the derived default must be truncated
// to fit or dataset creation 422s.
const MAX_DATASET_NAME_LENGTH = 20;

export default {
  name: 'OnboardingKnowledgeOptionsStep',
  components: {
    Spinner,
    KnowledgeProcessingModal,
  },
  // Browser back / in-app navigation while documents are still processing
  // would orphan the work; the processing modal's own actions are the only
  // way out until it settles.
  beforeRouteLeave(to, from, next) {
    next(this.processingPhase !== 'working');
  },
  data() {
    return {
      isRestoring: false,
      milestoneStamped: false,
      graphEnabled: true,
      imagesEnabled: true,
      // Chunk-size/overlap and delimiter are mutually exclusive splitting
      // strategies — only the selected method's value is ever sent.
      splitMethod: 'chunk',
      chunkWordSize: 900,
      chunkOverlapWordSize: 75,
      // Literal escape text, not an actual newline byte — a raw newline is
      // invisible/stripped in a single-line text input. Resolved back to a
      // real newline in the store before it's sent (knowledge.js).
      delimiter: '\\n',
    };
  },
  computed: {
    ...mapGetters('onboardingKnowledge', [
      'stagedFiles',
      'datasetId',
      'documents',
      'isCreatingDataset',
      'isUploading',
      'hasReadyDocument',
      'processingPhase',
      'readyCount',
      'totalCount',
      'overallProgress',
      'discardedFailures',
    ]),
    accountId() {
      return this.$route.params.accountId;
    },
    isBusy() {
      return this.isCreatingDataset || this.isUploading;
    },
    // After the dataset is auto-deleted its documents are gone from the store;
    // the modal still lists them so the user can see what failed.
    modalDocuments() {
      return this.processingPhase === 'allFailed' && !this.datasetId
        ? this.discardedFailures
        : this.documents;
    },
    // Retrying re-uploads from the original File objects, which only exist
    // until the page is refreshed.
    canRetry() {
      return this.stagedFiles.length > 0;
    },
    // What gets persisted for resume: the dataset plus the documents that
    // really reached the server (local-* ids are placeholders for uploads
    // still in flight).
    sessionSnapshot() {
      return {
        datasetId: this.datasetId,
        // Dropped files have no server-side document to remember; they are
        // re-derived on restore from `staged` and the saved ids.
        docs: this.documents.filter(
          doc =>
            !String(doc.id).startsWith('local-') && doc.status !== 'dropped'
        ),
        staged: this.stagedFiles,
        dropped: this.documents.filter(doc => doc.status === 'dropped'),
      };
    },
    // graph_enabled/images_enabled are creation-time-only — once a dataset
    // already exists, changing these local toggles wouldn't do anything, so
    // disable them rather than implying they still take effect.
    hasCreatedDataset() {
      return !!this.datasetId;
    },
    isChunkOverlapInvalid() {
      return (
        this.splitMethod === 'chunk' &&
        this.chunkWordSize !== null &&
        this.chunkWordSize !== '' &&
        this.chunkOverlapWordSize !== null &&
        this.chunkOverlapWordSize !== '' &&
        Number(this.chunkOverlapWordSize) >= Number(this.chunkWordSize)
      );
    },
    isDelimiterInvalid() {
      return (
        this.splitMethod === 'delimiter' &&
        !!this.delimiter &&
        this.delimiter.length > 10
      );
    },
    // Continue on this page only ever starts creation + upload. Everything
    // after that (waiting, retrying, moving on) happens in the processing
    // modal, which blocks this page while it is open.
    canContinue() {
      return (
        this.processingPhase === 'idle' &&
        !this.hasCreatedDataset &&
        this.stagedFiles.length > 0
      );
    },
  },
  watch: {
    // Persist (or forget) the dataset so a refresh can resume it. A cleared
    // dataset id means it was discarded, so the record must go too.
    sessionSnapshot(snapshot) {
      if (snapshot.datasetId) {
        saveOnboardingKbSession(this.accountId, snapshot);
      } else {
        clearOnboardingKbSession(this.accountId);
      }
    },
    processingPhase(phase) {
      if (phase === 'working') {
        window.addEventListener('beforeunload', this.warnBeforeUnload);
      } else {
        window.removeEventListener('beforeunload', this.warnBeforeUnload);
      }
    },
    hasReadyDocument(newVal) {
      if (newVal && !this.milestoneStamped) {
        this.milestoneStamped = true;
        this.markMilestone('kb_created');
      }
    },
    // The onboardingKnowledge Vuex module is registered once for the whole
    // SPA session, not per-account — see the same reasoning on
    // KnowledgeBaseStep. `immediate` covers the initial mount too, not just
    // a later account switch.
    accountId: {
      immediate: true,
      handler() {
        this.milestoneStamped = false;
        this.resetIfAccountChanged();
      },
    },
  },
  async mounted() {
    // Push the suggested defaults to the store now, otherwise they'd only
    // apply once the user touches a field or switches split method.
    this.syncAdvancedUploadOptions();

    if (!this.stagedFiles.length && !this.hasCreatedDataset) {
      // A refresh wipes the in-memory state but not the dataset it created
      // (and may still be indexing). Resume it from the server rather than
      // sending the user back to step 1, where re-picking files would create
      // a second, separately billed dataset.
      const session = getOnboardingKbSession(this.accountId);
      if (session) {
        this.isRestoring = true;
        const restored = await this.restoreFromSession(session);
        this.isRestoring = false;
        if (restored) return;
        clearOnboardingKbSession(this.accountId);
      }
      // Nothing staged and nothing to resume (e.g. direct navigation to this
      // route) — pick files first rather than show an empty step.
      this.$router.replace({
        name: 'onboarding_welcome',
        params: { accountId: this.accountId },
      });
    }
  },
  beforeDestroy() {
    window.removeEventListener('beforeunload', this.warnBeforeUnload);
  },
  methods: {
    ...mapActions('onboardingKnowledge', [
      'createDataset',
      'uploadDocuments',
      'updateGraphEnabled',
      'updateImagesEnabled',
      'updateAdvancedUploadOptions',
      'resetIfAccountChanged',
      'discardDataset',
      'retryFailedDocuments',
      'removeFailedDocuments',
      'resumePolling',
      'restoreFromSession',
      'clearDiscardedFailures',
    ]),
    warnBeforeUnload(event) {
      event.preventDefault();
      // Required by some browsers to show their native "leave site?" prompt.
      event.returnValue = this.$t(
        'ONBOARDING_SHELL.STEPS.KNOWLEDGE_OPTIONS.PROCESSING.LEAVE_WARNING'
      );
    },
    goTo(name) {
      this.$router.push({ name, params: { accountId: this.accountId } });
    },
    async handleContinuePartial() {
      await this.removeFailedDocuments();
      this.goTo('onboarding_sandbox');
    },
    async handleStartOver() {
      await this.discardDataset();
      // Cleared here as well as by the sessionSnapshot watcher: that one runs
      // on the next tick, after step 1 would already have read the stale
      // record and bounced the user straight back to this step.
      clearOnboardingKbSession(this.accountId);
      this.goTo('onboarding_welcome');
    },
    handleTryAgain() {
      this.clearDiscardedFailures();
      this.startUpload();
    },
    handleChooseDifferent() {
      this.clearDiscardedFailures();
      this.goTo('onboarding_welcome');
    },
    ...mapActions('onboarding', ['markMilestone']),
    setSplitMethod(method) {
      this.splitMethod = method;
      // Clear the other method's fields so only one is ever sent.
      if (method === 'chunk') {
        this.delimiter = null;
        if (this.chunkWordSize === null) {
          this.chunkWordSize = 900;
        }
        if (this.chunkOverlapWordSize === null) {
          this.chunkOverlapWordSize = 75;
        }
      } else {
        this.chunkWordSize = null;
        this.chunkOverlapWordSize = null;
        if (this.delimiter === null) {
          this.delimiter = '\\n';
        }
      }
      this.syncAdvancedUploadOptions();
    },
    syncAdvancedUploadOptions() {
      // Gate strictly on splitMethod, not just on whichever fields happen to
      // be non-null — the two methods' fields default independently and
      // aren't guaranteed to be cleared, so this is the one place that must
      // guarantee only one method's value is ever sent.
      this.updateAdvancedUploadOptions({
        chunkWordSize: this.splitMethod === 'chunk' ? this.chunkWordSize : null,
        chunkOverlapWordSize:
          this.splitMethod === 'chunk' ? this.chunkOverlapWordSize : null,
        delimiter: this.splitMethod === 'delimiter' ? this.delimiter : null,
      });
    },
    async startUpload() {
      try {
        // Default dataset name derived from the first staged filename — the
        // standalone Knowledge feature requires the user to type one,
        // which is worse UX for a locked-nav wizard.
        const defaultName = this.stagedFiles[0].name
          .replace(/\.[^/.]+$/, '')
          .slice(0, MAX_DATASET_NAME_LENGTH);
        await this.createDataset(defaultName);
        await this.uploadDocuments(this.stagedFiles);
      } catch (error) {
        useAlert(
          error?.message ||
            this.$t('ONBOARDING_SHELL.STEPS.KNOWLEDGE_OPTIONS.UPLOAD_ERROR')
        );
      }
    },
    handleContinue() {
      if (!this.canContinue) return;
      this.startUpload();
    },
  },
};
</script>

<template>
  <div
    class="flex flex-col gap-4 p-6 md:p-10 w-full h-full"
    :class="{ invisible: isRestoring }"
  >
    <h2 class="text-xl font-medium text-slate-900 dark:text-slate-100">
      {{ $t('ONBOARDING_SHELL.STEPS.KNOWLEDGE_OPTIONS.HEADING') }}
    </h2>
    <p class="text-sm text-slate-500 dark:text-slate-400">
      {{ $t('ONBOARDING_SHELL.STEPS.KNOWLEDGE_OPTIONS.SUBHEADING') }}
    </p>

    <!-- Additional options — lightrag only, ignored by the server for dify.
         Onboarding always creates on lightrag, so this always applies. -->
    <div class="my-2">
      <div
        class="p-4 border border-slate-100 dark:border-slate-700 rounded-md space-y-4"
      >
        <label class="flex items-start gap-2 cursor-pointer">
          <input
            v-model="graphEnabled"
            type="checkbox"
            class="mt-1"
            data-testid="kb_graph_enabled_checkbox"
            :disabled="hasCreatedDataset || isBusy"
            @change="updateGraphEnabled(graphEnabled)"
          />
          <span>
            <span class="block font-medium text-sm">{{
              $t(
                'ONBOARDING_SHELL.STEPS.KNOWLEDGE_OPTIONS.ADVANCED_OPTIONS.GRAPH_ENABLED_LABEL'
              )
            }}</span>
            <span class="block text-xs text-slate-500 dark:text-slate-400">{{
              $t(
                'ONBOARDING_SHELL.STEPS.KNOWLEDGE_OPTIONS.ADVANCED_OPTIONS.GRAPH_ENABLED_DESCRIPTION'
              )
            }}</span>
            <span
              class="block text-xs font-medium text-amber-600 dark:text-amber-400"
            >
              {{
                $t(
                  'ONBOARDING_SHELL.STEPS.KNOWLEDGE_OPTIONS.ADVANCED_OPTIONS.GRAPH_ENABLED_COST'
                )
              }}
            </span>
          </span>
        </label>

        <label class="flex items-start gap-2 cursor-pointer">
          <input
            v-model="imagesEnabled"
            type="checkbox"
            class="mt-1"
            data-testid="kb_images_enabled_checkbox"
            :disabled="hasCreatedDataset || isBusy"
            @change="updateImagesEnabled(imagesEnabled)"
          />
          <span>
            <span class="block font-medium text-sm">{{
              $t(
                'ONBOARDING_SHELL.STEPS.KNOWLEDGE_OPTIONS.ADVANCED_OPTIONS.IMAGES_ENABLED_LABEL'
              )
            }}</span>
            <span class="block text-xs text-slate-500 dark:text-slate-400">{{
              $t(
                'ONBOARDING_SHELL.STEPS.KNOWLEDGE_OPTIONS.ADVANCED_OPTIONS.IMAGES_ENABLED_DESCRIPTION'
              )
            }}</span>
            <span
              class="block text-xs font-medium text-amber-600 dark:text-amber-400"
            >
              {{
                $t(
                  'ONBOARDING_SHELL.STEPS.KNOWLEDGE_OPTIONS.ADVANCED_OPTIONS.IMAGES_ENABLED_COST'
                )
              }}
            </span>
          </span>
        </label>

        <div>
          <label class="block text-sm font-medium mb-1">{{
            $t(
              'ONBOARDING_SHELL.STEPS.KNOWLEDGE_OPTIONS.ADVANCED_OPTIONS.SPLIT_METHOD_LABEL'
            )
          }}</label>
          <div class="flex gap-4">
            <label class="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="onboardingSplitMethod"
                value="chunk"
                :checked="splitMethod === 'chunk'"
                :disabled="hasCreatedDataset || isBusy"
                @change="setSplitMethod('chunk')"
              />
              {{
                $t(
                  'ONBOARDING_SHELL.STEPS.KNOWLEDGE_OPTIONS.ADVANCED_OPTIONS.SPLIT_METHOD_CHUNK_LABEL'
                )
              }}
            </label>
            <label class="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="onboardingSplitMethod"
                value="delimiter"
                :checked="splitMethod === 'delimiter'"
                :disabled="hasCreatedDataset || isBusy"
                @change="setSplitMethod('delimiter')"
              />
              {{
                $t(
                  'ONBOARDING_SHELL.STEPS.KNOWLEDGE_OPTIONS.ADVANCED_OPTIONS.SPLIT_METHOD_DELIMITER_LABEL'
                )
              }}
            </label>
          </div>
        </div>

        <div
          v-if="splitMethod === 'chunk'"
          class="grid grid-cols-1 sm:grid-cols-2 gap-4"
        >
          <div>
            <label class="block text-sm font-medium mb-1">{{
              $t(
                'ONBOARDING_SHELL.STEPS.KNOWLEDGE_OPTIONS.ADVANCED_OPTIONS.CHUNK_WORD_SIZE_LABEL'
              )
            }}</label>
            <input
              v-model="chunkWordSize"
              type="number"
              min="75"
              max="6000"
              class="w-full"
              data-testid="kb_chunk_word_size_input"
              :disabled="hasCreatedDataset || isBusy"
              @change="syncAdvancedUploadOptions"
            />
            <p class="text-xs text-slate-500 dark:text-slate-400 mt-1">
              {{
                $t(
                  'ONBOARDING_SHELL.STEPS.KNOWLEDGE_OPTIONS.ADVANCED_OPTIONS.CHUNK_WORD_SIZE_HINT'
                )
              }}
            </p>
          </div>
          <div>
            <label class="block text-sm font-medium mb-1">{{
              $t(
                'ONBOARDING_SHELL.STEPS.KNOWLEDGE_OPTIONS.ADVANCED_OPTIONS.CHUNK_OVERLAP_LABEL'
              )
            }}</label>
            <input
              v-model="chunkOverlapWordSize"
              type="number"
              min="0"
              max="1500"
              class="w-full"
              data-testid="kb_chunk_overlap_input"
              :disabled="hasCreatedDataset || isBusy"
              @change="syncAdvancedUploadOptions"
            />
            <p class="text-xs text-slate-500 dark:text-slate-400 mt-1">
              {{
                $t(
                  'ONBOARDING_SHELL.STEPS.KNOWLEDGE_OPTIONS.ADVANCED_OPTIONS.CHUNK_OVERLAP_HINT'
                )
              }}
            </p>
            <p v-if="isChunkOverlapInvalid" class="text-xs text-red-500 mt-1">
              {{
                $t(
                  'ONBOARDING_SHELL.STEPS.KNOWLEDGE_OPTIONS.ADVANCED_OPTIONS.CHUNK_OVERLAP_VALIDATION_ERROR'
                )
              }}
            </p>
          </div>
        </div>

        <div v-else>
          <label class="block text-sm font-medium mb-1">{{
            $t(
              'ONBOARDING_SHELL.STEPS.KNOWLEDGE_OPTIONS.ADVANCED_OPTIONS.DELIMITER_LABEL'
            )
          }}</label>
          <input
            v-model="delimiter"
            type="text"
            class="w-32"
            data-testid="kb_delimiter_input"
            :disabled="hasCreatedDataset || isBusy"
            @change="syncAdvancedUploadOptions"
          />
          <p class="text-xs text-slate-500 dark:text-slate-400 mt-1">
            {{
              $t(
                'ONBOARDING_SHELL.STEPS.KNOWLEDGE_OPTIONS.ADVANCED_OPTIONS.DELIMITER_HINT'
              )
            }}
          </p>
          <p v-if="isDelimiterInvalid" class="text-xs text-red-500 mt-1">
            {{
              $t(
                'ONBOARDING_SHELL.STEPS.KNOWLEDGE_OPTIONS.ADVANCED_OPTIONS.DELIMITER_VALIDATION_ERROR'
              )
            }}
          </p>
        </div>
      </div>
    </div>

    <div
      v-if="isCreatingDataset"
      class="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400"
    >
      <Spinner size="small" color-scheme="primary" />
      {{ $t('ONBOARDING_SHELL.STEPS.KNOWLEDGE_OPTIONS.CREATING_DATASET') }}
    </div>

    <div class="flex-1" />

    <div class="flex justify-end">
      <woot-button
        :disabled="!canContinue || isChunkOverlapInvalid || isDelimiterInvalid"
        @click="handleContinue"
      >
        {{ $t('ONBOARDING_SHELL.CONTINUE_BUTTON') }}
      </woot-button>
    </div>

    <KnowledgeProcessingModal
      :phase="processingPhase"
      :documents="modalDocuments"
      :ready-count="readyCount"
      :total-count="totalCount"
      :progress="overallProgress"
      :can-retry="canRetry"
      @continue="goTo('onboarding_sandbox')"
      @continuePartial="handleContinuePartial"
      @retryFailed="retryFailedDocuments"
      @keepWaiting="resumePolling"
      @startOver="handleStartOver"
      @tryAgain="handleTryAgain"
      @chooseDifferent="handleChooseDifferent"
    />
  </div>
</template>
